import { cn } from "@/utils";
import { useState } from "react";

const SIZE = {
  sm: "size-8 text-ds-caption",
  md: "size-10 text-ds-small",
  lg: "size-16 text-ds-lead",
  xl: "size-20 text-ds-h4",
} as const;

/** Iniciais a partir do nome: no máximo duas letras ("Maria Silva" → "MS"). */
const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase() ?? "")
    .join("");

export interface AvatarProps {
  /** Nome da pessoa: vira as iniciais sem foto e o `alt` com foto. */
  name: string;
  /** Endereço da foto. Sem ela — ou se ela falhar ao carregar —, as iniciais. */
  src?: string | null;
  /**
   * `sm` 32px (lista densa) · `md` 40px (linha, cabeçalho) · `lg` 64px (ficha)
   * · `xl` 80px (perfil em destaque).
   */
  size?: keyof typeof SIZE;
  /**
   * A foto é só ilustração quando o nome já está escrito ao lado: aí ela vira
   * `alt=""` e o leitor de tela não lê o nome duas vezes.
   */
  decorative?: boolean;
  className?: string;
}

/**
 * Foto de uma pessoa, com as iniciais no lugar quando não há foto.
 *
 * Círculo com borda de 1px (`ds-border`): sem ela, foto de fundo claro some no
 * cartão off-white. As iniciais usam `ds-brand-700` sobre `ds-brand-100`
 * (6,3:1) — dá para ler mesmo no tamanho `sm`.
 */
export const Avatar = ({
  name,
  src,
  size = "md",
  decorative = false,
  className,
}: AvatarProps) => {
  // Foto quebrada (link expirado, arquivo apagado) cai nas iniciais em vez de
  // mostrar o ícone de imagem partida do navegador.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showPhoto = !!src && src !== failedSrc;

  return (
    <span
      role={decorative || showPhoto ? undefined : "img"}
      aria-label={decorative || showPhoto ? undefined : name}
      aria-hidden={decorative && !showPhoto ? true : undefined}
      className={cn(
        "border-ds-border inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border",
        "font-ds-sans bg-ds-brand-100 text-ds-brand-700 font-semibold select-none",
        SIZE[size],
        className
      )}
    >
      {showPhoto ? (
        <img
          src={src}
          alt={decorative ? "" : name}
          onError={() => setFailedSrc(src)}
          className="size-full object-cover"
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
};
